import { useState, useEffect } from 'react';
import { History, Search, Image, Clock, CheckCircle, XCircle, Upload, Loader2, RefreshCw } from 'lucide-react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { Button, Input } from '../components/ui';
import './HistoryPage.css';

interface GenerationLog {
    id: string;
    userId: string;
    shopId: string | null;
    productId: string | null;
    model: string;
    promptUsed: string;
    status: 'success' | 'rejected' | 'uploaded';
    createdAt: Date;
}

type StatusFilter = 'all' | 'success' | 'rejected' | 'uploaded';

export function HistoryPage() {
    const { userProfile, isAdmin } = useAuth();
    const [logs, setLogs] = useState<GenerationLog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

    useEffect(() => {
        loadLogs();
    }, [userProfile?.uid, isAdmin]);

    const loadLogs = async () => {
        if (!userProfile?.uid) {
            console.log('[History] No userProfile.uid, skipping load');
            return;
        }

        setIsLoading(true);
        setError('');

        console.log('[History] Loading logs for user:', userProfile.uid, 'isAdmin:', isAdmin);

        try {
            const logsRef = collection(db, 'logs');
            let logsQuery;

            // Admins see all logs, users see only their own
            if (isAdmin) {
                console.log('[History] Admin mode: fetching all logs');
                logsQuery = query(logsRef, orderBy('createdAt', 'desc'));
            } else {
                console.log('[History] User mode: fetching logs for userId:', userProfile.uid);
                logsQuery = query(
                    logsRef,
                    where('userId', '==', userProfile.uid),
                    orderBy('createdAt', 'desc')
                );
            }

            const snapshot = await getDocs(logsQuery);
            console.log('[History] Query returned', snapshot.size, 'documents');
            const logsData: GenerationLog[] = [];

            snapshot.forEach((doc) => {
                const data = doc.data();
                logsData.push({
                    id: doc.id,
                    userId: data.userId,
                    shopId: data.shopId,
                    productId: data.productId,
                    model: data.model,
                    promptUsed: data.promptUsed,
                    status: data.status,
                    createdAt: data.createdAt?.toDate() || new Date(),
                });
            });

            setLogs(logsData);
            console.log('[History] Loaded', logsData.length, 'logs successfully');
        } catch (err: any) {
            console.error('[History] Error loading logs:', err);
            console.error('[History] Error code:', err.code);
            console.error('[History] Error message:', err.message);
            setError('Erreur lors du chargement de l\'historique: ' + (err.message || 'Erreur inconnue'));
        } finally {
            setIsLoading(false);
        }
    };

    const filteredLogs = logs.filter((log) => {
        // Status filter
        if (statusFilter !== 'all' && log.status !== statusFilter) return false;

        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const promptMatches = log.promptUsed?.toLowerCase().includes(query) || false;
            const modelMatches = log.model?.toLowerCase().includes(query) || false;
            return promptMatches || modelMatches;
        }

        return true;
    });

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle size={16} className="status-icon status-success" />;
            case 'rejected':
                return <XCircle size={16} className="status-icon status-rejected" />;
            case 'uploaded':
                return <Upload size={16} className="status-icon status-uploaded" />;
            default:
                return <Image size={16} />;
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'success':
                return 'Générée';
            case 'rejected':
                return 'Rejetée';
            case 'uploaded':
                return 'Uploadée';
            default:
                return status;
        }
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('fr-FR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        }).format(date);
    };

    const formatModel = (model: string | undefined | null) => {
        if (!model) return 'Inconnu';
        if (model.includes('gemini')) return 'Gemini 2.0';
        if (model.includes('dall-e')) return 'DALL-E 3';
        return model;
    };

    // Stats
    const stats = {
        total: logs.length,
        success: logs.filter((l) => l.status === 'success').length,
        uploaded: logs.filter((l) => l.status === 'uploaded').length,
        rejected: logs.filter((l) => l.status === 'rejected').length,
    };

    return (
        <div className="history-page animate-fadeIn">
            <header className="page-header">
                <div>
                    <h1 className="page-title">Historique des Générations</h1>
                    <p className="page-subtitle">
                        {isAdmin ? 'Toutes les générations' : 'Vos générations d\'images'}
                    </p>
                </div>
                <Button
                    variant="secondary"
                    leftIcon={<RefreshCw size={18} />}
                    onClick={loadLogs}
                    disabled={isLoading}
                >
                    Actualiser
                </Button>
            </header>

            {/* Stats Cards */}
            <div className="stats-row">
                <div className="stat-card glass">
                    <Image size={20} />
                    <div className="stat-info">
                        <span className="stat-value">{stats.total}</span>
                        <span className="stat-label">Total</span>
                    </div>
                </div>
                <div className="stat-card glass stat-success">
                    <CheckCircle size={20} />
                    <div className="stat-info">
                        <span className="stat-value">{stats.success}</span>
                        <span className="stat-label">Générées</span>
                    </div>
                </div>
                <div className="stat-card glass stat-uploaded">
                    <Upload size={20} />
                    <div className="stat-info">
                        <span className="stat-value">{stats.uploaded}</span>
                        <span className="stat-label">Uploadées</span>
                    </div>
                </div>
                <div className="stat-card glass stat-rejected">
                    <XCircle size={20} />
                    <div className="stat-info">
                        <span className="stat-value">{stats.rejected}</span>
                        <span className="stat-label">Rejetées</span>
                    </div>
                </div>
            </div>

            {/* Filters */}
            <div className="history-toolbar glass">
                <Input
                    placeholder="Rechercher un prompt..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    leftIcon={<Search size={18} />}
                    className="search-input"
                />

                <div className="filter-buttons">
                    <button
                        className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('all')}
                    >
                        Tous
                    </button>
                    <button
                        className={`filter-btn ${statusFilter === 'success' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('success')}
                    >
                        <CheckCircle size={14} /> Générées
                    </button>
                    <button
                        className={`filter-btn ${statusFilter === 'uploaded' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('uploaded')}
                    >
                        <Upload size={14} /> Uploadées
                    </button>
                    <button
                        className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
                        onClick={() => setStatusFilter('rejected')}
                    >
                        <XCircle size={14} /> Rejetées
                    </button>
                </div>
            </div>

            {/* Logs List */}
            {isLoading ? (
                <div className="loading-state">
                    <Loader2 size={32} className="spin" />
                    <p>Chargement de l'historique...</p>
                </div>
            ) : error ? (
                <div className="error-state glass">
                    <p>{error}</p>
                    <Button variant="secondary" onClick={loadLogs}>Réessayer</Button>
                </div>
            ) : filteredLogs.length === 0 ? (
                <div className="empty-state glass">
                    <History size={64} className="empty-icon" />
                    <h2>{searchQuery || statusFilter !== 'all' ? 'Aucun résultat' : 'Aucune génération'}</h2>
                    <p>
                        {searchQuery || statusFilter !== 'all'
                            ? 'Aucune génération ne correspond à vos filtres.'
                            : 'Générez votre première image pour la voir apparaître ici.'}
                    </p>
                </div>
            ) : (
                <div className="logs-table glass">
                    <div className="table-header">
                        <div className="col-status">Statut</div>
                        <div className="col-prompt">Prompt</div>
                        <div className="col-model">Modèle</div>
                        <div className="col-date">Date</div>
                    </div>
                    <div className="table-body">
                        {filteredLogs.map((log) => (
                            <div key={log.id} className="table-row">
                                <div className="col-status">
                                    <span className={`status-badge status-${log.status}`}>
                                        {getStatusIcon(log.status)}
                                        {getStatusLabel(log.status)}
                                    </span>
                                </div>
                                <div className="col-prompt">
                                    <span className="prompt-text">{log.promptUsed || 'Prompt non disponible'}</span>
                                </div>
                                <div className="col-model">
                                    <span className="model-badge">{formatModel(log.model)}</span>
                                </div>
                                <div className="col-date">
                                    <Clock size={14} />
                                    {formatDate(log.createdAt)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
